import {Component, inject, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {PropertyDetailsModel} from '../../models/properties/properties.models';
import {GoogleMapsCenterModel} from '../../models/properties/maps.models';
import {PropertyUploadsModel} from '../../models/uploads/property-uploads.models';
import {Store} from '@ngrx/store';
import {AppState} from '../../../store/models/app-state.model';
import * as fromProperty from '../../../store/reducers/property';
import {PropertyUploadsField} from '../../enums/property.enums';
import {PriceQualifier} from '../../enums/price.enum';
import {Observable, of} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {environment} from '../../../../environments/environment';
import {catchError, map} from 'rxjs/operators';
import { SellerDetailsActions } from '../../../store/actions/user/seller-details.actions';
import { AgentDetailsActions } from '../../../store/actions/user/agent-details.actions';

@Component({
  selector: 'app-property-details',
  templateUrl: './property-details.component.html',
  styleUrls: ['./property-details.component.scss']
})
export class PropertyDetailsComponent implements OnInit {
  propertyDetails: PropertyDetailsModel;
  propertyUploads: PropertyUploadsModel[];
  propertyImages: PropertyUploadsModel[];
  propertyFloorPlan: PropertyUploadsModel;
  mapCenter: GoogleMapsCenterModel;
  isLoadingDetails = true;
  propertyId: number; // To limit loading property details only once, till property id changes - flag
  googleApiLoaded: Observable<boolean>;

  private actRoute = inject(ActivatedRoute);
  private store = inject(Store<AppState>);
  private httpClient = inject(HttpClient);
  private router = inject(Router);

  private readonly GOOGLE_API_URL = 'https://maps.googleapis.com/maps/api/js?key=';

  ngOnInit(): void {
    this.googleApiLoaded = this.httpClient.jsonp(`${this.GOOGLE_API_URL}${environment.googleApiKey}`, 'callback')
      .pipe(
        map(() => true),
        catchError((error) => {
          console.error(error);
          return of(false);
        }),
      );
    this.store.select(fromProperty.selectPropertyState).subscribe((data) => {
      this.isLoadingDetails = data.propertyDetails.isLoading || data.propertyUploads.isLoading;
      this.propertyDetails = data.propertyDetails.details;
      this.propertyUploads = data.propertyUploads.details;

      this.checkForRedirect();

      if (!this.isLoadingDetails && this.propertyId !== this.propertyDetails?.id) {
        this.propertyId = this.propertyDetails?.id;
        this.setPropertyImages();
        this.setPropertyMapDetails();
        this.getPropertyHostDetails();
      }
    });
  }

  haspPriceQualifier(): boolean {
    return Boolean(this.propertyDetails?.priceQualifierRelation
      && this.propertyDetails.priceQualifierRelation.id !== PriceQualifier.DEFAULT);
  }

  hasPropertyFloorPlan(): boolean {
    return Boolean(this.propertyFloorPlan?.thumbnails);
  }

  hasPropertyKeyFeatures(): boolean {
    return Boolean(this.propertyDetails?.propertyFeatures?.length > 0);
  }

  hasPropertyImages(): boolean {
    return Boolean(this.propertyImages?.length > 0);
  }

  private setPropertyImages(): void {
    this.propertyImages = this.propertyUploads?.filter((upload) => {
      return upload.field === PropertyUploadsField.PHOTO;
    });
    this.propertyFloorPlan = this.propertyUploads?.filter((upload) => {
      return upload.field === PropertyUploadsField.FLOORPLAN;
    }).pop();
  }

  private setPropertyMapDetails(): void {
    this.mapCenter = {
      lat: Number(this.propertyDetails?.mapLat),
      lng: Number(this.propertyDetails?.mapLon)
    };
  }

  private getPropertyHostDetails(): void {
    this.store.dispatch(SellerDetailsActions.loadSellerDetails({
      id: this.propertyDetails?.userId,
    }));
    this.store.dispatch(AgentDetailsActions.loadAgentDetails({
      id: this.propertyDetails?.lpeId,
    }));
  }

  checkForRedirect(): void {
    const viewingRequestsAllowed: boolean = this.propertyDetails?.propertyDetails?.viewingRequestsAllowed ?? true;

    if (!viewingRequestsAllowed) {
      this.router.navigate(['dashboard']);
    }
  }
}
